import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SASCard from './SASCard';
import { useLanguage } from '../../context/LanguageContext';

export default function BookCard({ book, onClick, index = 0 }) {
  const { language } = useLanguage();
  const [showDetails, setShowDetails] = useState(false);

  // Field Mapping for Bilingual Content
  const title = language === 'sindhi' 
    ? (book.title_sd || book.titleSindhi || book.title_en || book.title) 
    : (book.title_en || book.title || book.title_sd || book.titleSindhi);
    
  const author = language === 'sindhi' 
    ? (book.author_sd || book.author_en || book.author) 
    : (book.author_en || book.author || book.author_sd);

  const typeIcon = (t) => ({ book: '📗', journal: '📔', magazine: '📰' })[t] || '📄';

  const handleToggle = (e) => {
    e.stopPropagation();
    setShowDetails(!showDetails);
  };

  return (
    <SASCard
      variant="light"
      interactive
      hoverable
      delay={index * 0.04}
      onClick={onClick}
      className="card-royal"
      style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}
    >
      <div style={{ height: 4, background: 'linear-gradient(90deg, var(--sindhi-maroon), var(--sindhi-navy))' }} />
      
      {/* Cover Image */}
      <div style={{ height: 200, background: 'var(--sindhi-ivory)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        {(book.cover_image_url || book.cover || book.image) ? (
          <motion.img 
            whileHover={{ scale: 1.05 }} 
            transition={{ duration: 0.4 }} 
            src={book.cover_image_url || book.cover || book.image} 
            alt={title} 
            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '10px' }} 
          />
        ) : (
          <div style={{ textAlign: 'center', opacity: 0.2 }}>
            <div style={{ fontSize: 60 }}>{typeIcon(book.type || book.genre)}</div>
          </div>
        )}
      </div>

      {/* Main Info: Only Title and Author on Main View */}
      <div style={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <h3 style={{ 
          fontSize: 18, 
          fontWeight: 800, 
          color: 'var(--sindhi-navy)', 
          marginBottom: 8, 
          lineHeight: 1.3,
          fontFamily: language === 'sindhi' ? 'var(--font-sindhi)' : 'var(--font-body)',
          direction: language === 'sindhi' ? 'rtl' : 'ltr'
        }}>
          {title}
        </h3>
        
        {author && (
          <div style={{ 
            fontSize: 13, 
            color: 'var(--sindhi-text-muted)', 
            display: 'flex', 
            alignItems: 'center', 
            gap: 8, 
            marginTop: 'auto',
            fontFamily: language === 'sindhi' ? 'var(--font-sindhi)' : 'var(--font-body)',
            direction: language === 'sindhi' ? 'rtl' : 'ltr',
            justifyContent: language === 'sindhi' ? 'flex-end' : 'flex-start'
          }}>
            <span style={{ fontWeight: 600 }}>{author}</span> <span style={{ opacity: 0.5 }}>✍️</span>
          </div>
        )}
        
        <button 
          onClick={handleToggle}
          style={{
            marginTop: 12,
            background: 'var(--sindhi-cream)',
            border: 'none',
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            color: 'var(--sindhi-navy)',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--sindhi-cream)'}
        >
          {showDetails ? (language === 'sindhi' ? 'تفصيل لڪايو' : 'Hide Details') : (language === 'sindhi' ? 'تفصيل ڏسو' : 'View Details')}
        </button>
      </div>

      {/* Details View (Expandable) - Information Hiding */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{ overflow: 'hidden', background: 'var(--sindhi-ivory)', borderTop: '1px solid var(--sindhi-cream)' }}
          >
            <div style={{ padding: 16, fontSize: 13, color: 'var(--sindhi-text-muted)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {book.year && <div><strong>Year:</strong> {book.year}</div>}
              {book.language && <div style={{ textTransform: 'capitalize' }}><strong>Language:</strong> {book.language}</div>}
              {book.type && <div style={{ textTransform: 'capitalize' }}><strong>Type:</strong> {book.type}</div>}
              {book.publisher && <div><strong>Publisher:</strong> {book.publisher}</div>}
              {book.isbn && <div><strong>ISBN:</strong> {book.isbn}</div>}
              {book.pageCount && <div><strong>Pages:</strong> {book.pageCount}</div>}
              {book.downloads !== undefined && <div><strong>Downloads:</strong> {book.downloads}</div>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </SASCard>
  );
}
